import React from 'react';
import { Typography, TypographyProps, useTheme } from '@mui/material';

interface ParsedTextProps extends TypographyProps {
    children: string;
    heading?: boolean;
}

export const ParsedText: React.FC<ParsedTextProps> = ({ children, heading = false, sx, ...rest }) => {
    const theme = useTheme();
    
    const parsePatterns = [
        {
            pattern: /~#(.*?)#~/gm,
            style: { color: theme.palette.primary.main, fontWeight: 'bold' },
            transform: (text: string) => text.replace(/~#/g, '').replace(/#~/g, ','),
        },
        {
            pattern: /~%(.*?)%~/gm,
            style: { color: '#BDAE40', fontWeight: 'bold' },
            transform: (text: string) => text.replace(/~%/g, '').replace(/%~/g, ''),
        },
        {
            pattern: /~(.*?)~/gm,
            style: { color: theme.palette.primary.main },
            transform: (text: string) => text.replace(/~/g, ''),
        },
        {
            pattern: /#(.*?)#/gm,
            style: heading 
                ? { fontFamily: 'Lemon-Milk-Pro-Bold' }
                : { fontFamily: 'Plus Jakarta Sans', fontWeight: 600 },
            transform: (text: string) => text.replace(/#/g, ''),
        },
        {
            pattern: /%(.*?)%/gm,
            style: { color: '#BDAE40' },
            transform: (text: string) => text.replace(/%/g, ''),
        },
    ];

    const parseText = (text: string): React.ReactNode[] => {
        let result: React.ReactNode[] = [];
        let lastIndex = 0;

        // Find all matches for all patterns
        let matches: Array<{
            match: RegExpExecArray;
            patternIndex: number;
            style: any;
            transform: (text: string) => string;
        }> = [];

        parsePatterns.forEach((pattern, patternIndex) => {
            const regex = new RegExp(pattern.pattern.source, pattern.pattern.flags);
            let match;
            while ((match = regex.exec(text)) !== null) {
                matches.push({
                    match,
                    patternIndex,
                    style: pattern.style,
                    transform: pattern.transform,
                });
            }
        });

        // Sort matches by position
        matches.sort((a, b) => a.match.index - b.match.index);

        // Process matches and build result
        matches.forEach((matchInfo, index) => {
            const { match, style, transform } = matchInfo;
            
            // Add text before match
            if (match.index > lastIndex) {
                result.push(text.substring(lastIndex, match.index));
            }

            // Add styled match
            const transformedText = transform(match[0]);
            result.push(
                <Typography
                    key={`match-${index}`}
                    component="span"
                    sx={style}
                >
                    {transformedText}
                </Typography>
            );

            lastIndex = match.index + match[0].length;
        });

        // Add remaining text
        if (lastIndex < text.length) {
            result.push(text.substring(lastIndex));
        }

        return result.length > 0 ? result : [text];
    };

    const parsedContent = parseText(children);

    return (
        <Typography component="span" sx={sx} {...rest}>
            {parsedContent}
        </Typography>
    );
};

interface TypographyRichProps extends TypographyProps {
    children: string;
    heading?: boolean;
}

const TypographyRich: React.FC<TypographyRichProps> = ({ children, heading = false, ...rest }) => {
    return (
        <ParsedText heading={heading} {...rest}>
            {children}
        </ParsedText>
    );
};

export default TypographyRich;
